import { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import type { ClusterNode } from '@/types';

interface Props {
  node: ClusterNode;
  action: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const ACTION_CONFIG: Record<string, { title: string; description: string; destructive: boolean; confirmText?: string }> = {
  reboot: {
    title: 'Reboot Node',
    description: 'This will reboot the node. Running workloads will be disrupted.',
    destructive: false,
  },
  shutdown: {
    title: 'Shutdown Node',
    description: 'This will shut down the node. It will not come back online automatically.',
    destructive: true,
  },
  drain: {
    title: 'Drain Node',
    description: 'This will cordon the node and evict all pods. DaemonSet pods will remain.',
    destructive: false,
  },
  cordon: {
    title: 'Cordon Node',
    description: 'This will mark the node as unschedulable. Existing pods will continue running.',
    destructive: false,
  },
  uncordon: {
    title: 'Uncordon Node',
    description: 'This will mark the node as schedulable again. New pods can be placed on it.',
    destructive: false,
  },
  reset: {
    title: 'Reset Node',
    description: 'This will factory reset the node, wiping all data. This action is irreversible.',
    destructive: true,
    confirmText: 'Type the node hostname to confirm',
  },
};

export default function NodeActionDialog({ node, action, onConfirm, onCancel, isLoading }: Props) {
  const [confirmInput, setConfirmInput] = useState('');
  const config = ACTION_CONFIG[action] ?? { title: action, description: '', destructive: false };
  const needsConfirmInput = config.confirmText;
  const canConfirm = needsConfirmInput ? confirmInput === node.name : true;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-surface border border-surface-elevated rounded-xl w-full max-w-md mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <div className="flex items-center gap-2">
            {config.destructive && <AlertTriangle size={16} className="text-status-error" />}
            <h3 className="text-silver-bright font-semibold">{config.title}</h3>
          </div>
          <button onClick={onCancel} className="text-silver-muted hover:text-silver-bright transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-silver-muted">{config.description}</p>

          <div className="bg-surface-deep/50 rounded-lg px-4 py-3">
            <div className="text-xs text-silver-muted">Target Node</div>
            <div className="text-sm text-silver-bright font-mono">{node.name}</div>
          </div>

          {needsConfirmInput && (
            <div className="space-y-1.5">
              <label className="text-xs text-silver-muted">{config.confirmText}</label>
              <input
                type="text"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder={node.name}
                className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright font-mono placeholder:text-silver-muted/30 focus:border-gold focus:outline-none"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-surface-elevated">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm || isLoading}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              config.destructive
                ? 'bg-status-error text-white hover:bg-status-error/80'
                : 'bg-gold text-surface-deep hover:bg-gold-light'
            }`}
          >
            {isLoading ? 'Processing...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
